import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const reauthenticate = (password) => {
    if (!auth.currentUser || !auth.currentUser.email) return Promise.reject("Non connecté");
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    return reauthenticateWithCredential(auth.currentUser, credential);
  };

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const updateUserEmail = (newEmail) => {
    if (!auth.currentUser) return Promise.reject("Non connecté");
    return updateEmail(auth.currentUser, newEmail);
  };

  const updateUserPassword = (newPassword) => {
    if (!auth.currentUser) return Promise.reject("Non connecté");
    return updatePassword(auth.currentUser, newPassword);
  };

  const sendVerification = async (user = auth.currentUser) => {
    if (!user) return Promise.reject(new Error("Aucun utilisateur"));

    try {
      const apiEndpoint = import.meta.env.VITE_VERIFICATION_API_URL || 'https://masseterclass.vercel.app/api/send-verification';
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName || ''
        })
      });

      if (response.ok) {
        return;
      }
      console.warn("API d'email personnalisée indisponible, envoi via Firebase...");
    } catch (apiErr) {
      console.warn("Erreur API email personnalisé, repli sur Firebase:", apiErr);
    }

    return sendEmailVerification(user);
  };

  const resetPassword = async (email) => {
    try {
      const apiEndpoint = import.meta.env.VITE_VERIFICATION_API_URL || 'https://masseterclass.vercel.app/api/send-verification';
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          type: 'resetPassword'
        })
      });

      if (response.ok) {
        return;
      }
      console.warn("API d'email personnalisée indisponible pour le reset, envoi via Firebase...");
    } catch (apiErr) {
      console.warn("Erreur API email reset, repli sur Firebase:", apiErr);
    }

    return sendPasswordResetEmail(auth, email);
  };

  const googleSignIn = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    updateUserEmail,
    updateUserPassword,
    reauthenticate,
    sendVerification,
    resetPassword,
    googleSignIn,
    applyActionCode: (code) => applyActionCode(auth, code),
    verifyPasswordResetCode: (code) => verifyPasswordResetCode(auth, code),
    confirmPasswordReset: (code, newPassword) => confirmPasswordReset(auth, code, newPassword)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
